import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useControlTypeLabel } from 'src/pages/assessments/forms/useControlTypeLabel';

import { useRiskScoreFormatters } from '@/hooks/useRiskScore';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';

import { decorateWithControlType, getParentTitle } from './helpers';
import type {
  OversightAssessmentResultFields,
  OversightAssessmentResultRegisterFields,
} from './types';

export const useLabelledFields = (
  records: OversightAssessmentResultFields[] | undefined,
  configIds: string[] = []
) => {
  const { t: at } = useTranslation('common', {
    keyPrefix: 'assessmentResults',
  });
  const { t } = useTranslation(['common'], {
    keyPrefix: 'complianceMonitoringAssessment',
  });
  const status = t('status');
  const assessmentResultTypes = at('resultTypes', { returnObjects: true });
  const getControlTypeLabel = useControlTypeLabel();
  const { getByValue: getByResultValue } = useRating(
    'performance_result',
    'oversight'
  );
  const { getByValue: getEffectivenessByValue } = useRating(
    'effectiveness',
    'oversight'
  );
  const { resolveImpact, resolveLikelihood } = useRiskRatingResolver(
    'oversight',
    { configType: 'compliance_monitoring', configIds }
  );
  const getFormatters = useRiskScoreFormatters();

  return useMemo<OversightAssessmentResultRegisterFields[] | undefined>(() => {
    const ratingFns = {
      document_oversight_result: (d: OversightAssessmentResultFields) =>
        getByResultValue(d.Rating),
      obligation_oversight_result: (d: OversightAssessmentResultFields) =>
        getByResultValue(d.Rating),
      control_test_oversight_result: (d: OversightAssessmentResultFields) =>
        getEffectivenessByValue(d.OverallEffectiveness),
      risk_uncontrolled_oversight_result: (
        d: OversightAssessmentResultFields
      ) => {
        const rating = d.Rating;
        const controlType = d.ControlType;
        if (rating == null || controlType == null) {
          return;
        }

        const { getInherentLabel } = getFormatters({
          inherentRating: rating,
          inherentLikelihood: d.Likelihood,
          inherentImpact: d.Impact,
        });

        return {
          label: getInherentLabel(),
        };
      },

      risk_controlled_oversight_result: (
        d: OversightAssessmentResultFields
      ) => {
        const rating = d.Rating;
        const controlType = d.ControlType;
        if (rating == null || controlType == null) {
          return;
        }

        const { getResidualLabel } = getFormatters({
          residualRating: rating,
          residualLikelihood: d.Likelihood,
          residualImpact: d.Impact,
        });

        return {
          label: getResidualLabel(),
        };
      },
    };

    const likelihoodFns = {
      document_oversight_result: (_d: OversightAssessmentResultFields) => null,
      obligation_oversight_result: (_d: OversightAssessmentResultFields) =>
        null,
      control_test_oversight_result: (_d: OversightAssessmentResultFields) =>
        null,
      risk_controlled_oversight_result: (d: OversightAssessmentResultFields) =>
        resolveLikelihood(
          d.Likelihood,
          'ConfigId' in d ? d.ConfigId : undefined
        ),
      risk_uncontrolled_oversight_result: (
        d: OversightAssessmentResultFields
      ) =>
        resolveLikelihood(
          d.Likelihood,
          'ConfigId' in d ? d.ConfigId : undefined
        ),
    };

    const impactFns = {
      document_oversight_result: (_d: OversightAssessmentResultFields) => null,
      obligation_oversight_result: (_d: OversightAssessmentResultFields) =>
        null,
      control_test_oversight_result: (_d: OversightAssessmentResultFields) =>
        null,
      risk_controlled_oversight_result: (d: OversightAssessmentResultFields) =>
        resolveImpact(d.Impact, 'ConfigId' in d ? d.ConfigId : undefined),
      risk_uncontrolled_oversight_result: (
        d: OversightAssessmentResultFields
      ) => resolveImpact(d.Impact, 'ConfigId' in d ? d.ConfigId : undefined),
    };

    return records?.map((d) => {
      const assessmentStatus = d.oversightAssessments.find(
        (a) => a.oversightAssessment
      )?.oversightAssessment?.Status;

      return {
        ...d,
        AssessmentTitle:
          d.oversightAssessments.find((a) => a.oversightAssessment)
            ?.oversightAssessment?.Title || '-',
        TypeLabelled: decorateWithControlType(
          assessmentResultTypes[
            d.__typename as keyof typeof assessmentResultTypes
          ],
          getControlTypeLabel,
          d
        ),
        ParentTitle: getParentTitle(d) || '-',
        RatingLabelled:
          ratingFns[d.__typename as keyof typeof ratingFns](d)?.label || '-',
        ImpactLabelled:
          impactFns[d.__typename as keyof typeof impactFns](d)?.label || '-',
        LikelihoodLabelled:
          likelihoodFns[d.__typename as keyof typeof likelihoodFns](d)?.label ||
          '-',
        StartDate: d.oversightAssessments.find((a) => a.oversightAssessment)
          ?.oversightAssessment?.StartDate,
        ActualCompletionDate: d.oversightAssessments.find(
          (a) => a.oversightAssessment
        )?.oversightAssessment?.ActualCompletionDate,
        CompletedByUser:
          d.oversightAssessments.find((a) => a.oversightAssessment)
            ?.oversightAssessment?.completedByUser?.FriendlyName || '-',
        Rationale: d.Rationale,
        TestDate: d.TestDate,
        Status: assessmentStatus ?? '-',
        StatusLabelled: assessmentStatus ? status[assessmentStatus] : '-',
        originalResult: d,
      };
    });
  }, [
    records,
    assessmentResultTypes,
    getControlTypeLabel,
    getByResultValue,
    getEffectivenessByValue,
    resolveLikelihood,
    resolveImpact,
    getFormatters,
    status,
  ]);
};
